/**
 * Serviço para transmissão ao vivo usando Daily.co
 */

import { ref, push, set, get } from './phpDataAdapter';
import { database } from '../config/backend';

export interface LiveStream {
  id: string;
  streamId: string;
  roomUrl: string;
  authorId: string;
  authorName: string;
  title?: string;
  description?: string;
  status: 'starting' | 'live' | 'ended';
  startedAt: string;
  endedAt?: string;
  viewerCount?: number;
  isPublic: boolean;
}

const DAILY_API_URL = 'https://api.daily.co/v1';
const DAILY_API_KEY = process.env.REACT_APP_DAILY_API_KEY || '';

/**
 * Cria uma nova sala de transmissão no Daily.co
 */
async function createDailyRoom(roomName?: string): Promise<{ url: string; name: string }> {
  try {
    const response = await fetch(`${DAILY_API_URL}/rooms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DAILY_API_KEY}`,
      },
      body: JSON.stringify({
        name: roomName || `live-${Date.now()}`,
        privacy: 'public',
        properties: {
          enable_chat: true,
          enable_screenshare: false,
          enable_recording: false,
          exp: Math.floor(Date.now() / 1000) + 3600, // Expira em 1 hora
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Erro ao criar sala: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      url: data.url,
      name: data.name,
    };
  } catch (error: any) {
    console.error('Erro ao criar sala Daily.co:', error);
    throw new Error('Não foi possível criar a transmissão. Verifique a configuração da API.');
  }
}

/**
 * Cria uma nova transmissão ao vivo
 */
export async function createLiveStream(
  userId: string,
  userName: string,
  title?: string,
  description?: string,
  isPublic: boolean = true
): Promise<LiveStream> {
  try {
    // Criar sala no Daily.co
    const { url: roomUrl, name: streamId } = await createDailyRoom();

    // Criar registro no servidor PHP/MySQL
    const streamRef = push(ref(database, 'liveStreams'));
    const streamId_db = streamRef.key!;

    const liveStream: LiveStream = {
      id: streamId_db,
      streamId,
      roomUrl,
      authorId: userId,
      authorName: userName,
      title,
      description,
      status: 'starting',
      startedAt: new Date().toISOString(),
      isPublic,
    };

    await set(streamRef, liveStream);

    return liveStream;
  } catch (error: any) {
    console.error('Erro ao criar transmissão:', error);
    throw error;
  }
}

/**
 * Atualiza o status de uma transmissão
 */
export async function updateStreamStatus(
  streamId: string,
  status: 'starting' | 'live' | 'ended'
): Promise<void> {
  try {
    const streamRef = ref(database, `liveStreams/${streamId}`);
    const updates: any = {
      status,
    };

    if (status === 'ended') {
      updates.endedAt = new Date().toISOString();
    }

    await set(ref(database, `liveStreams/${streamId}/status`), status);
    if (status === 'ended') {
      await set(ref(database, `liveStreams/${streamId}/endedAt`), new Date().toISOString());
    }
  } catch (error: any) {
    console.error('Erro ao atualizar status da transmissão:', error);
    throw error;
  }
}

/**
 * Busca uma transmissão pelo ID
 */
export async function getLiveStream(streamId: string): Promise<LiveStream | null> {
  try {
    const streamRef = ref(database, `liveStreams/${streamId}`);
    const snapshot = await get(streamRef);

    if (!snapshot.exists()) {
      return null;
    }

    return {
      id: streamId,
      ...snapshot.val(),
    } as LiveStream;
  } catch (error: any) {
    console.error('Erro ao buscar transmissão:', error);
    return null;
  }
}

/**
 * Lista transmissões ativas
 */
export async function getActiveStreams(nutritionistId?: string): Promise<LiveStream[]> {
  try {
    const streamsRef = ref(database, 'liveStreams');
    const snapshot = await get(streamsRef);

    if (!snapshot.exists()) {
      return [];
    }

    const streams = snapshot.val() as Record<string, any>;
    const streamsList: LiveStream[] = Object.entries(streams)
      .map(([id, data]) => ({
        id,
        ...data,
      }))
      .filter((stream: LiveStream) => {
        // Filtrar apenas streams ativas
        if (stream.status !== 'live' && stream.status !== 'starting') {
          return false;
        }

        // Filtrar por nutricionista se especificado
        if (nutritionistId && stream.authorId !== nutritionistId) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Ordenar por data de início (mais recente primeiro)
        return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
      });

    return streamsList;
  } catch (error: any) {
    console.error('Erro ao listar transmissões:', error);
    return [];
  }
}

/**
 * Finaliza uma transmissão
 */
export async function endLiveStream(streamId: string): Promise<void> {
  try {
    await updateStreamStatus(streamId, 'ended');
  } catch (error: any) {
    console.error('Erro ao finalizar transmissão:', error);
    throw error;
  }
}




